import { Button } from "@worship-devo/ui";
import { type MutableRefObject, type ReactNode, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { create } from "zustand";
import { persist } from "zustand/middleware";

import { analytics } from "~/analytics";
import { useWindowEventHandler } from "~/hooks/useWindowEventHandler";
import { cn } from "~/lib/utils";

type PromptStore = {
  promptEvent: BeforeInstallPromptEvent | undefined;
  setPromptEvent: (e: BeforeInstallPromptEvent | undefined) => void;
  timesShown: number;
  setTimesShown: (timesShown: number) => void;
};
const useInstallPromptStore = create<PromptStore>()(
  persist(
    (set) => {
      return {
        promptEvent: undefined,
        setPromptEvent: (promptEvent) => {
          set({ promptEvent });
        },
        timesShown: 1,
        setTimesShown(timesShown) {
          set({ timesShown });
        },
      };
    },
    {
      name: "twi.installPromptStore",
      partialize: (s) => {
        return { timesShown: s.timesShown };
      },
    },
  ),
);
export function useCaptureInstallPrompt() {
  useWindowEventHandler("beforeinstallprompt", (e) => {
    analytics.track({ event: "Before Install Prompt Event Captured" });
    useInstallPromptStore.getState().setPromptEvent(e);
  });
}
type InstallButtonProps = {
  hasSentAnalyticRef: MutableRefObject<boolean>;
  variant?: "default" | "settingsLink";
  icon?: ReactNode;
  className?: string;
  hideLabel?: boolean;
};

export function InstallButton({
  hasSentAnalyticRef,
  variant = "default",
  icon,
  className,
  hideLabel = false,
}: InstallButtonProps) {
  const { t } = useTranslation("app");
  const { promptEvent, setPromptEvent, timesShown, setTimesShown } = useInstallPromptStore();
  useEffect(() => {
    if (promptEvent && !hasSentAnalyticRef.current) {
      analytics.track({ event: "Add To Home Screen Button Shown" });
      hasSentAnalyticRef.current = true;
    }
  }, [promptEvent, hasSentAnalyticRef]);

  if (!promptEvent) {
    return null;
  }
  return (
    <Button
      className={cn(
        variant === "settingsLink"
          ? "h-auto w-full justify-start rounded-none border-0 bg-transparent p-2 text-white hover:cursor-pointer hover:bg-transparent"
          : "w-full font-medium text-base",
        className,
      )}
      data-testid="install-button"
      onClick={() => {
        analytics.track({ event: "Add To Home Screen Button Pressed" });
        void promptEvent.prompt().then((result) => {
          analytics.track({ event: "Install Prompt Result Received", properties: { ...result, timesShown } });
          //install prompt events can only be used once. remove the event to avoid attempted re-use
          setPromptEvent(undefined);
          setTimesShown(timesShown + 1);
        });
      }}
    >
      {variant === "settingsLink" ? (
        <span className="flex items-center gap-x-3 text-base lg:text-sm">
          {icon}
          {hideLabel ? null : <span>{t("installButton.addToHomeScreen")}</span>}
        </span>
      ) : (
        t("installButton.addToHomeScreen")
      )}
    </Button>
  );
}
